/**
 * ModelStore — web GLB loading strategy.
 *
 * - Only models listed as `available` in the ArtMap (i.e. vendored under
 *   public/assets/ and recorded in public/assets/manifest.json) are ever
 *   fetched: no blind 404s, no console-error spam.
 * - Loaded GLBs are cached by URL; failures resolve to null (caller falls
 *   back to the placeholder) and warn once.
 * - Unit meshes stay instanced: when unit GLBs land, this store vends the
 *   cached scene whose geometry feeds the InstancedMesh — the instancing
 *   strategy in render/units.ts does not change, only the geometry source.
 * - CLONE RULE: load() returns the SHARED cached scene. Callers must
 *   scene.clone(true) before parenting it — three.js allows one parent per
 *   object, and per-view material tweaks (construction dimming) must not
 *   leak across views. Geometry stays shared (do not dispose it); clone
 *   materials per view and dispose those.
 *
 * PROTOTYPE MAPPING (web -> UE): GLBs are decimated exports of the UE-side
 * assets (see tools/vendor_models.py); LOD tiers/billboards plug in here later
 * for the 20,000+ unit target.
 */
import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type { ResolvedModel } from "../data/artmap";

export class ModelStore {
  private loader = new GLTFLoader();
  private cache = new Map<string, Promise<GLTF | null>>();
  private warned = new Set<string>();

  constructor(private baseUrl: string = import.meta.env.BASE_URL) {}

  /** Loadable URL for a resolved model, or null when it must use the placeholder. */
  urlFor(model: ResolvedModel): string | null {
    if (!model.available || !model.webPath) return null;
    return `${this.baseUrl}${model.webPath}`;
  }

  /**
   * Load (and cache) a GLB scene by explicit web path (e.g. an LOD tier
   * from ResolvedModel.lodPaths). Shares the scene cache with load().
   * Null on failure — the caller keeps its fallback geometry.
   */
  async loadByUrl(webPath: string): Promise<THREE.Group | null> {
    const gltf = await this.loadGltfUrl(`${this.baseUrl}${webPath}`);
    return gltf ? gltf.scene : null;
  }

  /** Load (and cache) the full GLB; null when not vendored or on failure. */
  private loadGltf(model: ResolvedModel): Promise<GLTF | null> {
    const url = this.urlFor(model);
    if (!url) return Promise.resolve(null);
    return this.loadGltfUrl(url);
  }

  /** Shared URL-keyed GLTF cache (single download per URL). */
  private loadGltfUrl(url: string): Promise<GLTF | null> {
    let pending = this.cache.get(url);
    if (!pending) {
      pending = this.loader.loadAsync(url).catch((e: unknown) => {
        if (!this.warned.has(url)) {
          this.warned.add(url);
          console.warn(`[models] failed to load ${url}: ${String(e)}`);
        }
        return null;
      });
      this.cache.set(url, pending);
    }
    return pending;
  }

  /**
   * Load (and cache) the GLB scene for a resolved model. Resolves to the
   * SHARED cached scene — clone before parenting (see clone rule above).
   * Null when the model is not vendored or the load fails — the caller
   * keeps its placeholder.
   */
  async load(model: ResolvedModel): Promise<THREE.Group | null> {
    const gltf = await this.loadGltf(model);
    return gltf ? gltf.scene : null;
  }

  /**
   * Load the skeletal animation clips embedded in a unit GLB (for the
   * real-clip swap in render/anim.ts). Resolves to [] when the model is
   * not vendored, has no clips, or fails to load — the caller keeps the
   * procedural placeholders. Shares the scene cache: no second download.
   */
  async loadAnimationClips(
    model: ResolvedModel,
  ): Promise<THREE.AnimationClip[]> {
    const gltf = await this.loadGltf(model);
    return gltf?.animations ?? [];
  }

  /**
   * Bone rest-pose local quaternions, keyed by lowercased bone/node name.
   * Animation retargeting (render/anim.ts) needs these: GLB tracks store
   * the FULL local rotation (rest + motion), so the rest orientation must
   * be removed before the motion is copied onto the placeholder rig.
   * Shares the scene cache: no second download.
   */
  async loadBoneRestQuats(
    model: ResolvedModel,
  ): Promise<Record<string, THREE.Quaternion>> {
    const gltf = await this.loadGltf(model);
    const out: Record<string, THREE.Quaternion> = {};
    if (!gltf) return out;
    gltf.scene.updateMatrixWorld(true);
    gltf.scene.traverse((obj) => {
      const skinned = obj as THREE.SkinnedMesh;
      if (!skinned.isSkinnedMesh || !skinned.skeleton) return;
      for (const bone of skinned.skeleton.bones) {
        const key = bone.name.toLowerCase();
        if (!(key in out)) out[key] = bone.quaternion.clone();
      }
    });
    return out;
  }

  /** Forget cached scenes (test hook / memory pressure). */
  clear(): void {
    this.cache.clear();
  }
}

/**
 * Extract one static (unskinned) BufferGeometry from a loaded GLB scene for
 * instanced rendering: every mesh's world-baked geometry merged into one.
 * Skinning/morph/extra attributes are dropped — the merged geometry renders
 * with a single standard material (faction tint), exactly like the capsule
 * it replaces. Returns null when the scene holds no usable geometry.
 *
 * Pure w.r.t. the input scene: works on clones, never mutates it.
 */
export function extractStaticGeometry(
  scene: THREE.Group,
): THREE.BufferGeometry | null {
  scene.updateMatrixWorld(true);
  const parts: THREE.BufferGeometry[] = [];
  const tmp = new THREE.Matrix4();
  scene.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const src = mesh.geometry as THREE.BufferGeometry | undefined;
    if (!src || !src.getAttribute("position")) return;
    const g = src.clone();
    // Drop skinning/morphs/secondary channels: unskinned single material.
    g.deleteAttribute("skinIndex");
    g.deleteAttribute("skinWeight");
    g.deleteAttribute("tangent");
    g.deleteAttribute("color");
    g.morphAttributes = {};
    tmp.copy(mesh.matrixWorld);
    g.applyMatrix4(tmp);
    if (!g.getAttribute("normal")) g.computeVertexNormals();
    if (!g.getAttribute("uv")) {
      const n = g.getAttribute("position").count;
      g.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(n * 2), 2));
    }
    parts.push(g);
  });
  if (parts.length === 0) return null;
  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  return merged;
}
